/**
 * The id of a Zaraz purpose
 */
export type ZarazPurposeId = string

export type Product = {
    // Product ID displayed on the product list.
    product_id: string
    // Product SKU displayed on the product list.
    sku: string
    // Product category displayed on the product list.
    category?: string
    // Product name displayed on the product list.
    name: string
    // Product brand displayed on the product list.
    brand?: string
    // Product variant displayed on the product list.
    variant?: string
    // Price of the product displayed on the product list.
    price: number
    // Quantity of a product displayed on the product list.
    quantity?: number
    // Name or serial number of coupon code associated with product displayed on the product list.
    coupon?: string
    // Product position in the product list (for example, 2).
    position?: number
}

export type Parameters = {
    // Product ID.
    product_id: string
    // Product SKU number.
    sku: string
    // Product category.
    category: string
    // Product name.
    name: string
    // Product brand name.
    brand: string
    // Product variant (depending on the product, it could be product color, size, etc.).
    variant: string
    // Product price.
    price: number
    // Product number of units.
    quantity: number
    // Name or serial number of coupon code associated with product.
    coupon: string
    // Product position in the product list (for example, 2).
    position: number
    // List of products displayed in the product list.
    products: Product[]
    // Checkout ID.
    checkout_id: string
    // Internal ID of order/transaction/purchase.
    order_id: string
    // Name of affiliate from which the order occurred.
    affiliation: string
    // Revenue with discounts and coupons added in.
    total: number
    // Revenue excluding shipping and tax.
    revenue: number
    // Cost of shipping for transaction.
    shipping: number
    // Total tax for transaction.
    tax: number
    // Total discount for transaction.
    discount: number
    // Name or serial number of coupon redeemed on the transaction-level.
    // coupon: string; // TODO: dedupe
    // Currency code for the transaction.
    currency: string
    // Total value of the product after quantity.
    value: number
    // Label for creative asset of promotion being tracked.
    creative: string
    // Product search term.
    query: string
    // The Number of the checkout step in the checkout process.
    step: number
    // The type of payment used.
    payment_type: string
}

export type ZarazEcommerceEventName = 'Product List Viewed' | 'Products Searched' | 'Product Clicked' | 'Product Added' | 'Product Added to Wishlist' | 'Product Removed' | 'Product Viewed' | 'Cart Viewed' | 'Checkout Started' | 'Checkout Step Viewed' | 'Checkout Step Completed' | 'Payment Info Entered' | 'Order Completed' | 'Order Updated' | 'Order Refunded' | 'Order Cancelled' | 'Clicked Promotion' | 'Viewed Promotion' | 'Shipping Info Entered'

export const useZaraz = () => {
    if (import.meta.server) {
        return {
            async track(eventName: string, eventProperties?: Record<string, any>) { },
            set(key: string, value: any, options?: {
                scope: 'page' | 'session' | 'persist'
            }) { },
            unset(key: string) { },
            ecommerce(eventName: ZarazEcommerceEventName, eventProperties: Partial<Parameters>) { },
            consent: {
                get(purposeId: ZarazPurposeId): boolean | undefined {
                    return undefined
                },
                set(consentPreferences: Record<ZarazPurposeId, boolean>) { },
                sendQueuedEvents(): undefined {}
            }
        }
    }

    const zaraz = window.zaraz

    async function track(eventName: string, eventProperties?: Record<string, any>) {
        if (!zaraz) return

        await zaraz.track(eventName, eventProperties)
    }

    function set(key: string, value: any, options?: {
        scope: 'page' | 'session' | 'persist'
    }) {
        zaraz?.set(key, value, options)
    }

    function unset(key: string) {
        zaraz?.set(key, undefined)
    }

    async function ecommerce(
        eventName: ZarazEcommerceEventName,
        eventProperties: Partial<Parameters>
    ) {
        if (!zaraz) return
        await zaraz.ecommerce(eventName, eventProperties)
    }

    const consent = {
        get(purposeId: ZarazPurposeId): boolean | undefined {
            return zaraz.consent.get(purposeId)
        },
        set(consentPreferences: Record<ZarazPurposeId, boolean>) {
            return zaraz.consent.set(consentPreferences)
        },
        sendQueuedEvents(): undefined {
            return zaraz.consent.sendQueuedEvents()
        }

    }

    return {
        track,
        set,
        unset,
        ecommerce,
        consent
    }
}
